import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * NexusCMS SSO landing page.
 * Nexus opens /embed-auth?embed_token=<jwt> inside an iframe. We exchange the single-use
 * embed_token for a normal access token, store it in sessionStorage (per-tab, avoids
 * clobbering the user's main login and does not rely on third-party cookies), and route
 * to the dashboard.
 */
export default function EmbedAuthPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, setUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const embedToken = searchParams.get('embed_token');

    if (!embedToken) {
      setError('Missing embed token');
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/auth/embed-exchange`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ embed_token: embedToken }),
          },
        );

        if (!res.ok) {
          const detail = await res.json().catch(() => ({}));
          throw new Error(detail?.detail || `Exchange failed (${res.status})`);
        }

        const { token, user } = await res.json();
        if (cancelled) return;

        // Per-tab session so we never overwrite the parent user's localStorage token.
        sessionStorage.setItem('token', token);
        sessionStorage.setItem('embedded', 'nexus');
        setToken(token);
        setUser(user);

        // Strip the token from the URL before landing on the dashboard.
        window.history.replaceState({}, '', '/embed-auth');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        if (!cancelled) setError(err.message || 'Authentication failed');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, setToken, setUser]);

  if (error) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        data-testid="embed-auth-error"
      >
        <div className="text-center max-w-md px-6">
          <p className="text-red-600 mb-3">{error}</p>
          <p className="text-sm text-muted-foreground">
            Please close this window and try again from your NexusCMS dashboard.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-gray-50"
      data-testid="embed-auth-loading"
    >
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-4" />
        <p className="text-muted-foreground">Opening Taptile…</p>
      </div>
    </div>
  );
}
